import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  name?: string;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error boundary that logs the full error + component stack to console
 * and shows a user-friendly fallback instead of a white screen.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    // Log full details — this survives minification because it uses the Error object
    console.error(
      `[ErrorBoundary${this.props.name ? `:${this.props.name}` : ''}] Caught error:`,
      error.message,
    );
    console.error('[ErrorBoundary] Stack:', error.stack);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '100vh', width: '100vw', background: '#F5F0EB',
          fontFamily: '"Geist", system-ui, sans-serif', padding: 40,
        }}>
          <div style={{
            maxWidth: 480, background: '#fff', borderRadius: 16, padding: '40px 44px',
            border: '1px solid #E5DDD5', boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center', background: '#FFF3EE',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C04A2A" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#3D3229', textAlign: 'center' }}>
              Something went wrong
            </h1>
            <p style={{ margin: 0, fontSize: 14, color: '#9E9189', textAlign: 'center', lineHeight: '20px' }}>
              The app encountered an error. Try refreshing the page.
            </p>
            <pre style={{
              margin: 0, padding: 12, borderRadius: 8, backgroundColor: '#F5F2EC',
              fontSize: 11, color: '#6B5E52', lineHeight: '16px', overflow: 'auto',
              maxHeight: 120, width: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {this.state.error.message}
            </pre>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#1A1714', border: 'none', borderRadius: 10, cursor: 'pointer',
                padding: '12px 0', fontSize: 15, fontWeight: 600, color: '#fff', width: '100%',
              }}
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
